import { AirCurtain } from './airCurtain';
import { Account } from './account';
import { AirCurtainGroup } from './airCurtainGroup';
import { Identity } from './identity';
import { Organization } from './organization';
import { Role } from './role';
import { Site } from './site';
import { SiteGroup } from './siteGroup';
import { System } from './system';
import { User } from './user';
import { UserGroup } from './userGroup';
import { Invitation } from './invitation';
import { Location } from './location';
import { Installation } from './installation';
import {
    getUserPolicies,
    getObjects,
    getShadow,
    getInstallations,
    getUnitIDs,
    getCognitoUser as getCognitoUserAPI,
} from './../api/webApplicationApi';
import { getListAirCurtainUserRelation } from './../api/airCurtainClientApi';
import { Bin } from './bin';

const systemID = '15140447-f0c3-405e-a789-6f040bf2d754';
const ownerRoleID = '40c461cb-8866-468c-8dfc-1a0bf28e5b9a';

export class ObjectManager {
    // simple scenario: (one organization, multiple sites)
    // list of assumptions:
    //      - identity has only one user (1:1)
    //      - organisation has one site group (1:1)
    //      - organization has no user group
    //      - site has only one air curtain group (1:1)
    //      - site has only one user group (1:1)
    //      - the group type is not used (generic group)
    //      - user head object type and ID are not used
    //      - user group head is always site and set to the site where the group is
    //      - roles are attached to user only (not to user groups)
    //      - roles cannot be edited or created
    //      - only roles defined at the system level are accessible to attach to user
    //      - only one role can be attached to a user (1:1)
    //      - policies are not visible or editable
    //      - the policies of the logged in user are availlable to allow/deny action in the UI

    private _identityID: string;
    private _email: string;
    private _isOwner: boolean;
    private _system: System;
    private _account: Account;
    private _organization: Organization;
    private _identity: Identity;
    private _user: User;
    private _owner: User;
    private _siteGroup: SiteGroup;
    private _users: Map<string, User> = new Map<string, User>();
    private _identities: Map<string, Identity> = new Map<string, Identity>();
    private _sites: Map<string, Site> = new Map<string, Site>();
    private _userGroups: Map<string, UserGroup> = new Map<string, UserGroup>();
    private _airCurtainGroups: Map<string, AirCurtainGroup> = new Map<
        string,
        AirCurtainGroup
    >();
    private _airCurtains: Map<string, AirCurtain> = new Map<
        string,
        AirCurtain
    >();
    private _roles: Map<string, Role> = new Map<string, Role>();
    private _userPolicies: Array<omPolicyPayload>;
    private _bin: Bin;
    private _invitations: Map<string, Invitation> = new Map<
        string,
        Invitation
    >();
    private _locations: Map<string, Location> = new Map<string, Location>();
    private _installations: Map<string, Installation> = new Map<string, Installation>();

    private _lastError: Error = undefined;

    public constructor(identityID: string, email: string) {
        this._identityID = identityID;
        this._email = email;
    }

    public async initialize(): Promise<boolean> {
        this._lastError = undefined;

        // concurrent load of System, Identity
        const getSystemRequest: GetRequest = {
            objectType: 'system',
            keys: [systemID],
        };
        const getIdentityRequest: GetRequest = {
            objectType: 'identity',
            keys: [this._identityID],
        };

        const getInviteRequest: GetRequest = {
            objectType: 'invitation',
            keys: [this._email],
        };

        let invitation: Invitation = undefined;
        let identity: Identity = undefined;

        let responses = await getObjects([
            getSystemRequest,
            getIdentityRequest,
            getInviteRequest,
        ]);
        responses.forEach((response) => {
            switch (response.objectType) {
                case 'system':
                    this._system = System.createFromObject(response.objects[0]);
                    break;
                case 'identity':
                    if (response.objects.length > 0) {
                        identity = Identity.createFromObject(
                            response.objects[0]
                        );
                    }
                    break;
                case 'invitation':
                    if (response.objects.length > 0) {
                        invitation = Invitation.createFromObject(
                            response.objects[0]
                        );
                    }
                    break;
            }
        });
        if (invitation != undefined) {
            if (identity == undefined) {
                //accept invitation
                const result = await this.acceptInvitation(
                    invitation,
                    this._identityID
                );
                if (result != undefined) {
                    identity = result;
                }
            } else {
                //delete invitation
                await this.deleteInvitation(invitation);
            }
        }

        // load roles
        if (identity != undefined) {
            this._identity = identity;
            //identity found load user and system roles
            // in simple scenario, one user per identity
            const getUserRequest: GetRequest = {
                objectType: 'user',
                keys: [this._identity.users[0]],
            };
            const getSystemRolesRequest: GetRequest = {
                objectType: 'role',
                keys: this._system.roles,
            };

            responses = await getObjects([
                getUserRequest,
                getSystemRolesRequest,
            ]);
            responses.forEach((response) => {
                switch (response.objectType) {
                    case 'user':
                        if (response.objects.length > 0) {
                            this._user = User.createFromObject(
                                response.objects[0]
                            );
                        }
                        break;
                    case 'role':
                        response.objects.forEach((obj: omRole) => {
                            this._roles.set(
                                obj.roleID,
                                Role.createFromObject(obj)
                            );
                        });
                        break;
                }
            });

            if (!this._user) {
                this._lastError = new Error(
                    'identity: ' +
                        this._identityID +
                        ' is denied access to the system'
                );
                return false;
            }
            await this.loadAccountAndAll();

            return true;
        }
        //no identity found, IT SHOULD HAVE BEEN CREATED ON INDIVIDUAL ACCEPTING INVITE
        this._lastError = new Error(
            'identity: ' +
                this._identityID +
                ' is unknown. Please check your email for an invitation'
        );
        return false;
    }

    private async acceptInvitation(
        invitation: Invitation,
        identityID: string
    ): Promise<Identity> {
        // get user from invite
        const user = new User(invitation.userID);
        let result = await user.load();
        if (!result) return undefined;

        // some validation
        if (user.invitationID != invitation.invitationID) return undefined;
        if (user.accountID != invitation.accountID) return undefined;

        //create identity
        const identity = new Identity(identityID);
        identity.email = invitation.invitationID;
        identity.addUser(user);
        result = await identity.save();
        if (!result) return undefined;

        // user mods (removing the reference to the invitationID will activate a trigger on the objUsers table)
        user.invitationID = undefined;
        user.identityID = identity.identityID;
        user.acceptedInvitationDate = new Date();
        result = await user.save();
        if (!result) return undefined;

        return identity;
    }

    private async deleteInvitation(invitation: Invitation): Promise<boolean> {
        // get user from invite
        const user = new User(invitation.userID);
        let result = await user.load();
        if (!result) return false;

        // some validation
        if (user.invitationID != invitation.invitationID) return false;
        if (user.accountID != invitation.accountID) return false;

        // user mods (removing the reference to the invitationID will activate a trigger on the objUsers table)
        user.invitationID = undefined;
        result = await user.save();
        if (!result) return false;

        return true;
    }

    public get lastError(): Error {
        return this._lastError;
    }

    public createAccount(name: string): Account {
        const account = Account.createNew();
        account.name = name;
        return account;
    }

    public createUser(name: string, firstName: string, lastName: string): User {
        const user = User.createNew();
        user.name = name;
        user.firstName = firstName;
        user.lastName = lastName;
        user.accountID = this._account.accountID;
        return user;
    }

    public createBin(): Bin {
        const bin = Bin.createNew();
        bin.accountID = this._account.accountID;
        return bin;
    }

    public createLocation(siteID: string, latitude: number, longitude: number, naturalGasRate: number, electricityRate: number, oilRate: number, powerCycles: number, address: string): Location {
        const location = new Location(siteID);
        location.latitude = latitude;
        location.longitude = longitude;
        location.naturalGasRate = naturalGasRate;
        location.electricityRate = electricityRate;
        location.oilRate = oilRate;
        location.powerCycles = powerCycles;
        location.postalCode = address;

        this._locations.set(siteID, location);
        return location;
    }

    public createOrganization(name: string): Organization {
        const organization = Organization.createNew();
        organization.name = name;
        organization.accountID = this._account.accountID;
        return organization;
    }

    public createUserGroup(name: string): UserGroup {
        const userGroup = UserGroup.createNew();
        userGroup.name = name;
        userGroup.accountID = this._account.accountID;
        return userGroup;
    }

    public createSiteGroup(name: string): SiteGroup {
        const siteGroup = SiteGroup.createNew();
        siteGroup.name = name;
        siteGroup.accountID = this._account.accountID;
        return siteGroup;
    }

    public createSite(name: string): Site {
        const site = Site.createNew();
        site.name = name;
        site.accountID = this._account.accountID;
        return site;
    }

    public createAirCurtainGroup(name: string): AirCurtainGroup {
        const airCurtainGroup = AirCurtainGroup.createNew();
        airCurtainGroup.name = name;
        airCurtainGroup.accountID = this._account.accountID;
        return airCurtainGroup;
    }

    public linkOwnerToAccount(owner: User): void {
        if (owner.accountID != this._account.accountID) {
            throw new Error("user doesn't belong to account");
        }

        this._account.ownerUserID = owner.userID;
        owner.headObjectType = 'account';
        owner.headObjectID = this._account.accountID;
    }

    public linkBinToAccount(bin: Bin): void {
        if (bin.accountID != this._account.accountID) {
            throw new Error("bin doesn't belong to account");
        }
        this._account.binID = bin.binID;
    }

    public linkOrganizationToAccount(organization: Organization): void {
        if (organization.accountID != this._account.accountID) {
            throw new Error("organization doesn't belong to account");
        }

        this._account.organizationID = organization.organizationId;
    }

    public addSiteGroupToOrganization(
        organization: Organization,
        siteGroup: SiteGroup
    ): void {
        organization.addSiteGroup(siteGroup);
        siteGroup.parentObjectType = 'organization';
        siteGroup.parentObjectID = organization.organizationId;
    }

    public removeSiteGroupFromOrganization(
        organization: Organization,
        siteGroup: SiteGroup
    ): void {
        organization.deleteSiteGroup(siteGroup);
        siteGroup.parentObjectType = undefined;
        siteGroup.parentObjectID = undefined;
    }

    public addUserGroupToSite(site: Site, userGroup: UserGroup): void {
        site.addUserGroup(userGroup);
        userGroup.parentObjectType = 'site';
        userGroup.parentObjectID = site.siteID;
        userGroup.headObjectType = 'site';
        userGroup.headObjectID = site.siteID;
    }

    public removeUserGroupFromSite(site: Site, userGroup: UserGroup): void {
        site.deleteUserGroup(userGroup);
        userGroup.parentObjectType = undefined;
        userGroup.parentObjectID = undefined;
    }

    public addUserToIdentity(identity: Identity, user: User): void {
        identity.addUser(user);
        user.identityID = identity.identityID;
    }

    public removeUserFromIdentity(identity: Identity, user: User): void {
        identity.deleteUser(user);
        user.identityID = undefined;
    }

    public addSiteToSiteGroup(siteGroup: SiteGroup, site: Site): void {
        siteGroup.addSite(site);
        site.siteGroupID = siteGroup.siteGroupID;
    }

    public removeSiteFromSiteGroup(siteGroup: SiteGroup, site: Site): void {
        siteGroup.deleteSite(site);
        site.siteGroupID = undefined;
    }

    public addAirCurtainGroupToSite(
        site: Site,
        airCurtainGroup: AirCurtainGroup
    ): void {
        site.addAirCurtainGroup(airCurtainGroup);
        airCurtainGroup.parentObjectType = 'site';
        airCurtainGroup.parentObjectID = site.siteID;
    }

    public removeAirCurtainGroupFromSite(
        site: Site,
        airCurtainGroup: AirCurtainGroup
    ): void {
        site.deleteAirCurtainGroup(airCurtainGroup);
        airCurtainGroup.parentObjectType = undefined;
        airCurtainGroup.parentObjectID = undefined;
    }

    public addUserToUserGroup(userGroup: UserGroup, user: User): void {
        userGroup.addUser(user);
        user.addUserGroup(userGroup);
    }

    public removeUserFromUserGroup(userGroup: UserGroup, user: User): void {
        userGroup.deleteUser(user);
        user.deleteUserGroup(userGroup);
    }

    public addAirCurtainToAirCurtainGroup(
        airCurtainGroup: AirCurtainGroup,
        airCurtain: AirCurtain
    ): void {
        airCurtainGroup.addAirCurtain(airCurtain);
        airCurtain.addAirCurtainGroup(airCurtainGroup);
    }

    public removeAirCurtainFromAirCurtainGroup(
        airCurtainGroup: AirCurtainGroup,
        airCurtain: AirCurtain
    ): void {
        airCurtainGroup.deleteAirCurtain(airCurtain);
        airCurtain.deleteAirCurtainGroup(airCurtainGroup);
    }

    public addAirCurtainToUser(user: User, airCurtain: AirCurtain): void {
        user.addAirCurtain(airCurtain);
    }

    public removeAirCurtainFromUser(user: User, airCurtain: AirCurtain): void {
        user.deleteAirCurtain(airCurtain);
    }

    public addRoleToUser(user: User, role: Role): void {
        user.addRole(role);
    }

    public removeRoleFromUser(user: User, role: Role): void {
        user.deleteRole(role);
    }

    public getSystem(): System {
        return this._system;
    }

    public getOrganization(): Organization {
        return this._organization;
    }

    public getAccount(): Account {
        return this._account;
    }

    public isOwner(): boolean {
        return this._isOwner;
    }

    public getOwner(): User {
        return this._owner;
    }

    public getCurrentIdentity(): Identity {
        return this._identity;    
    }

    public getSiteGroup(): SiteGroup {
        return this._siteGroup;
    }

    public getSite(siteID: string): Site {
        if (this._sites.has(siteID)) {
            return this._sites.get(siteID);
        }
        return undefined;
    }

    getAllSites(): Array<Site> {
        return Array.from(this._sites, ([key, value]) => value);
    }

    public getUserGroup(siteGroupID: string): UserGroup {
        if (this._userGroups.has(siteGroupID)) {
            return this._userGroups.get(siteGroupID);
        }
        return undefined;
    }

    public async getUser(userID: string): Promise<User> {
        if (this._users.has(userID)) {
            return this._users.get(userID);
        } else {
            const user = new User(userID);
            const result = await user.load();
            if (result) {
                this._users.set(userID, user);
                return user;
            }
            return undefined;
        }
    }

    public async getAllUsers(): Promise<Array<User>> {
        const users: Array<string> = [];
        this._userGroups.forEach((userGroup: UserGroup) => {
            userGroup.users.forEach((userId: string) => {
                users.push(userId);
            });
        });
        await this.loadUsers(users);
        return Array.from(this._users, ([key, value]) => value);
    }

    public async getAllUsersFromUserGroup(
        userGroup: UserGroup
    ): Promise<Array<User>> {
        await this.loadUsers(userGroup.users);
        const users = Array.from(this._users, ([key, value]) => value);
        return users.filter((user) => {
            return user.userGroups.includes(userGroup.userGroupID);
        });
    }

    public async getIdentity(identityID: string): Promise<Identity> {
        if (this._identities.has(identityID)) {
            return this._identities.get(identityID);
        } else {
            const identity = new Identity(identityID);
            const result = await identity.load();
            if (result) {
                this._identities.set(identityID, identity);
                return identity;
            }
            return undefined;
        }
    }

    public async getAllIdentities(): Promise<Array<Identity>> {
        const identities: Array<string> = [];
        this._users.forEach((user: User) => {
            //invitees have no identityID
            if (user.identityID) {
                identities.push(user.identityID);
            }
        });
        await this.loadIdentities(identities);
        return Array.from(this._identities, ([key, value]) => value);
    }

    public async getIdentities(
        identityIDs: Array<string>
    ): Promise<Array<Identity>> {
        await this.loadIdentities(identityIDs);
        const identities = Array.from(
            this._identities,
            ([key, value]) => value
        );
        return identities.filter((identity) => {
            return identityIDs.includes(identity.identityID);
        });
    }

    public getAirCurtainGroup(airCurtainID: string): AirCurtainGroup {
        if (this._airCurtainGroups.has(airCurtainID)) {
            return this._airCurtainGroups.get(airCurtainID);
        }
        return undefined;
    }

    public async getAirCurtain(airCurtainID: string): Promise<AirCurtain> {
        if (this._airCurtains.has(airCurtainID)) {
            return this._airCurtains.get(airCurtainID);
        } else {
            const airCurtain = new AirCurtain(airCurtainID);
            const result = await airCurtain.load();
            if (result) {
                this._airCurtains.set(airCurtainID, airCurtain);
                return airCurtain;
            }
            return undefined;
        }
    }

    public async getAllAirCurtains(): Promise<Array<AirCurtain>> {
        const airCurtains: Array<string> = [];
        this._airCurtainGroups.forEach((airCurtainGroup: AirCurtainGroup) => {
            airCurtainGroup.airCurtains.forEach((airCurtainId: string) => {
                if (!airCurtains.includes(airCurtainId)) {
                    airCurtains.push(airCurtainId);
                }
            });
        });
        await this.loadAirCurtains(airCurtains);
        return Array.from(this._airCurtains, ([key, value]) => value);
    }

    public async getAllAirCurtainsFromAirCurtainGroup(
        airCurtainGroup: AirCurtainGroup
    ): Promise<Array<AirCurtain>> {
        await this.loadAirCurtains(airCurtainGroup.airCurtains);
        const airCurtains = Array.from(
            this._airCurtains,
            ([key, value]) => value
        );
        return airCurtains.filter((airCurtain) => {
            //TODO check: newly assigned air curtains have airCurtainGroupID but no airCurtainGroups array
            return airCurtain.airCurtainGroups?.includes(airCurtainGroup.airCurtainGroupID);
        });
    }

    public getAllRoles(): Array<Role> {
        return Array.from(this._roles, ([key, value]) => value);
    }

    public getRole(roleID: string): Role {
        if (this._roles.has(roleID)) {
            return this._roles.get(roleID);
        }
        return undefined;
    }

    public getUserPolicies(): Array<omPolicyPayload> {
        return this._userPolicies;
    }

    // public getAllTrashesFromBin():Array<omTrash> {
    //     return this._bin.trashes;
    // }

    // public async addTrashToBin(trash:omTrash):Promise<boolean>{
    //     this._bin.addTrash(trash);
    //     return await this._bin.save();
    // }

    // public async removeTrashFromBin(trash:omTrash):Promise<boolean>{
    //     this._bin.deleteTrash(trash);
    //     return await this._bin.save();
    // }

    public get bin(): Bin {
        return this._bin;
    }

    public async getInvitation(invitationID: string): Promise<Invitation> {
        if (this._invitations.has(invitationID)) {
            return this._invitations.get(invitationID);
        } else {
            const invitation = new Invitation(invitationID);
            const result = await invitation.load();
            if (result) {
                this._invitations.set(invitationID, invitation);
                return invitation;
            }
            return undefined;
        }
    }

    public async getUnitIDs(orderId: string): Promise<string[]> {
        return await getUnitIDs(orderId);
    }   

    public async getInstallations(requestedInstallations: omInstallationRequest[]): Promise<Map<string, Installation>> {
        const requestInstallationObjects: omInstallation[] = [];
        for (const requestedInstallation of requestedInstallations) {
            const key = requestedInstallation.siteID + "|" + requestedInstallation.thingName;
            if (!this._installations.has(key)) {
                const installation = {
                    thingName: requestedInstallation.thingName,
                    siteID: requestedInstallation.siteID,
                    applicationType: undefined as any,
                    openingWidth: undefined as any,
                    openingHeight: undefined as any,
                    doorMountingHeight: undefined as any,
                    interiorTemperatureSetPointWinter: undefined as any,
                    interiorTemperatureSetPointSummer: undefined as any,
                    interiorRelativeHumidityWinter: undefined as any,
                    interiorRelativeHumiditySummer: undefined as any,
                    model: undefined as any,
                    heatingEnergyType: undefined as any,
                    coolingEnergyType: undefined as any,
                    calculateHeatingSaving: undefined as any,
                    calculateCoolingSaving: undefined as any,
                    hasHeating: undefined as any,
                    hasTemperatureSensor: undefined as any,
                    heatingPower: undefined as any,
                    unconditionedHeatingTemperatureSetPoint: undefined as any,
                };
    
                requestInstallationObjects.push(installation);
            }
        }

        if (requestInstallationObjects.length) {
            const responseInstallations = await getInstallations(requestInstallationObjects);
            for (const installation of responseInstallations) {
                const key = installation.siteID + "|" + installation.thingName;
                this._installations.set(key, Installation.createFromObject(installation));
            }
        }

        return this._installations;
    }

    public async getInstallation(siteID: string, airCurtainID: string): Promise<Installation> {
        const key = siteID + "|" + airCurtainID;
        if (this._installations.has(key)) {
            return this._installations.get(key);
        } else {
            const installation = new Installation(airCurtainID, siteID);
            const result = await installation.load();
            if (result) {
                this._installations.set(key, installation);
                return installation;
            }
            return installation;
        }
    }

    public async getLocation(siteID: string): Promise<Location> {
        if (this._locations.has(siteID)) {
            return this._locations.get(siteID);
        } else {
            const location = new Location(siteID);
            const result = await location.load();
            if (result) {
                this._locations.set(siteID, location);
                return location;
            }
            return undefined;
        }
    }    

    public async createDefaultAccount(
        email: string,
        accountName: string,
        userName: string,
        userFirstName: string,
        userLastName: string,
        organizationName: string,
        siteName: string
    ): Promise<boolean> {
        const promises = []; //will save all in batch

        if (this._roles.size == 0) {
            const getSystemRolesRequest: GetRequest = {
                objectType: 'role',
                keys: this._system.roles,
            };
            const responses = await getObjects([getSystemRolesRequest]);
            responses[0].objects.forEach((obj: omRole) => {
                this._roles.set(obj.roleID, Role.createFromObject(obj));
            });
        }

        // create identity
        this._identity = new Identity(this._identityID);
        this._identity.email = email;

        // create account
        this._account = this.createAccount(accountName);

        // create bin
        this._bin = this.createBin();

        // create user (owner)
        this._user = this.createUser(userName, userFirstName, userLastName);

        //link identity with user
        this.addUserToIdentity(this._identity, this._user);

        //link admin role to user
        this.addRoleToUser(this._user, this._roles.get(ownerRoleID));

        //link account with owner
        this.linkOwnerToAccount(this._user);

        //link account to bin
        this.linkBinToAccount(this._bin);

        //save user to have permissions set on backend
        let result = await this._identity.save(); // create identity first, api will check that we create a user for this identity
        if (!result) console.log(this._identity.lastError);
        // await this.sleep(1000);
        result = await this._user.save();
        if (!result) console.log(this._user.lastError);

        this._userPolicies = await getUserPolicies([]);

        this._users.set(this._user.userID, this._user);
        this._owner = this._user;
        this._isOwner = true;

        // create organization
        this._organization = this.createOrganization(organizationName);

        // create site group
        this._siteGroup = this.createSiteGroup('Sites');

        // create default site
        const defaultSite = this.createSite(siteName);
        this._sites.set(defaultSite.siteID, defaultSite);

        // create default user group
        const defaultUserGroup = this.createUserGroup('Users');
        this._userGroups.set(defaultUserGroup.userGroupID, defaultUserGroup);

        // create default air curtain group
        const defaultAirCurtainGroup = this.createAirCurtainGroup(
            'Air Curtains'
        );
        this._airCurtainGroups.set(
            defaultAirCurtainGroup.airCurtainGroupID,
            defaultAirCurtainGroup
        );

        //link user to user group
        this.addUserToUserGroup(defaultUserGroup, this._user)

        //link account with organization
        this.linkOrganizationToAccount(this._organization);

        //link organization with site group
        this.addSiteGroupToOrganization(this._organization, this._siteGroup);

        //link site to site group
        this.addSiteToSiteGroup(this._siteGroup, defaultSite);

        //link air curtain group to site
        this.addAirCurtainGroupToSite(defaultSite, defaultAirCurtainGroup);

        //link user group to site
        this.addUserGroupToSite(defaultSite, defaultUserGroup);

        /*
        //get all user air curtains from mobile api, attach to user and to default air curtain group
        if (!this._identity.email.includes('berner.com')) {
            // berner users can't attach air curtains to their own account, else they will be unavaillable to others as we all use the sames
            // admin will attach them manually if required
            const airCurtains = await this.getUserAirCurtainsFromMobileAPI();
            airCurtains.forEach((airCurtain) => {
                this._airCurtains.set(airCurtain.airCurtainID, airCurtain);
                this.addAirCurtainToUser(this._user, airCurtain);
                this.addAirCurtainToAirCurtainGroup(
                    defaultAirCurtainGroup,
                    airCurtain
                );
                promises.push(airCurtain.saveProperties());
            });
        }
        */

        //save all
        promises.push(this._account.save());
        promises.push(this._bin.save());
        promises.push(this._user.save());
        promises.push(this._organization.save());
        promises.push(defaultUserGroup.save());
        promises.push(this._siteGroup.save());
        promises.push(defaultSite.save());
        promises.push(defaultAirCurtainGroup.save());
        const results = await Promise.all(promises);

        //load shadows
        promises.length = 0;
        if (this._airCurtains.size > 0) {
            this._airCurtains.forEach((airCurtain) => {
                promises.push(airCurtain.refreshShadow());
            });
            await Promise.all(promises);
        }

        return !results.includes(false);
    }

    private async loadAccountAndAll() {
        const promises: Array<Promise<any>> = [];

        // concurrently load account, usr policies and user air curtains
        const getAccountRequest: GetRequest = {
            objectType: 'account',
            keys: [this._user.accountID],
        };
        const getUserAirCurtainsRequest: GetRequest = {
            objectType: 'airCurtain',
            keys: this._user.airCurtains,
        };
        promises.push(
            getObjects([getAccountRequest, getUserAirCurtainsRequest])
        );
        promises.push(getUserPolicies([]));
        this._user.airCurtains.forEach((airCurtainID) => {
            const getShadowRequest: GetShadowRequest = { airCurtainID };
            promises.push(getShadow(getShadowRequest));
        });

        let responses = await Promise.all(promises);
        let getObjectResponses = responses[0];
        this._userPolicies = responses[1];

        const shadows: Map<string, AirCurtainShadow2> = new Map<
            string,
            AirCurtainShadow2
        >();
        for (let i = 2; i < responses.length - 1; i++) {
            shadows.set(responses[i].airCurtainID, responses[i]);
        }

        getObjectResponses.forEach((response: any) => {
            switch (response.objectType) {
                case 'account':
                    this._account = Account.createFromObject(
                        response.objects[0]
                    );
                    break;
                case 'airCurtain':
                    response.objects.forEach((obj: omAirCurtain) => {
                        this._airCurtains.set(
                            obj.airCurtainID,
                            AirCurtain.createFromObject(
                                obj,
                                shadows.get(obj.airCurtainID)
                            )
                        );
                    });

                    break;
            }
        });

        //is owner logged in?
        this._isOwner = this._account.ownerUserID == this._user.userID;
        if (this._isOwner) {
            this._owner = this._user;
        } else {
            this._owner = new User(this._account.ownerUserID);
            await this._owner.load();
            this._users.set(this._account.ownerUserID, this._owner);
        }

        //load organization
        const organizationID = this._account.organizationID;
        this._organization = new Organization(organizationID);
        await this._organization.load();

        //load site group
        const siteGroupID = this._organization.siteGroups[0]; // in simple scenario, one site group only
        this._siteGroup = new SiteGroup(siteGroupID);
        await this._siteGroup.load();

        //load sites
        promises.length = 0;
        const getSitesRequest: GetRequest = {
            objectType: 'site',
            keys: this._siteGroup.sites,
        };
        promises.push(getObjects([getSitesRequest]));

        responses = await Promise.all(promises);

        const getSitesResponse = responses[0];

        getSitesResponse.forEach((response: any) => {
            response.objects.forEach((obj: omSite) => {
                this._sites.set(
                    obj.siteID,
                    Site.createFromObject(obj)
                );
            });
        });

        const getSiteGroupsRequest: GetRequest = {
            objectType: 'airCurtainGroup',
            keys: [],
        };
        const getUserGroupsRequest: GetRequest = {
            objectType: 'userGroup',
            keys: [],
        };
        const getBinRequest: GetRequest = {
            objectType: 'bin',
            keys: [this._account.binID],
        };

        promises.length = 0;
        this._sites.forEach((site) => {
            getSiteGroupsRequest.keys.push(site.airCurtainGroups[0]); // in simple scenario, one air curtain group per site
            getUserGroupsRequest.keys.push(site.userGroups[0]); // in simple scenario, one user group per site
        });

        const getObjectsRequest: Array<GetRequest> = [];
        getObjectsRequest.push(getSiteGroupsRequest);
        getObjectsRequest.push(getUserGroupsRequest);
        if (this._account.binID != undefined) {
            getObjectsRequest.push(getBinRequest);
        }

        getObjectResponses = await getObjects(getObjectsRequest);
        getObjectResponses.forEach((response: any) => {
            switch (response.objectType) {
                case 'airCurtainGroup':
                    response.objects.forEach((obj: omAirCurtainGroup) => {
                        this._airCurtainGroups.set(
                            obj.airCurtainGroupID,
                            AirCurtainGroup.createFromObject(obj)
                        );
                    });
                    break;
                case 'userGroup':
                    response.objects.forEach((obj: omUserGroup) => {
                        this._userGroups.set(
                            obj.userGroupID,
                            UserGroup.createFromObject(obj)
                        );
                    });
                    break;
                case 'bin':
                    if (response.objects.length > 0) {
                        this._bin = Bin.createFromObject(response.objects[0]);
                    }
                    break;
            }
        });
    }

    public async loadAirCurtains(
        airCurtainIDs: Array<string>
    ): Promise<boolean> {
        // load in batch of 100 the air curtains ID passed to the function
        const promises: Array<Promise<any>> = [];
        const keys: Array<string> = [];
        airCurtainIDs.forEach((airCurtainID) => {
            if (!this._airCurtains.has(airCurtainID)) {
                keys.push(airCurtainID);
                if (keys.length == 100) {
                    const getAirCurtainsRequest: GetRequest = {
                        objectType: 'airCurtain',
                        keys: [...keys],
                    };
                    promises.push(getObjects([getAirCurtainsRequest]));
                    keys.length = 0;
                }
            }
        });
        if (keys.length > 0) {
            const getAirCurtainsRequest: GetRequest = {
                objectType: 'airCurtain',
                keys: [...keys],
            };
            promises.push(getObjects([getAirCurtainsRequest]));
            keys.length = 0;
        }

        if (promises.length > 0) {
            const responses = await Promise.all(promises);
            for (let i = 0; i < responses.length; i++) {
                const response = responses[i][0];
                if (response) {
                    response.objects.forEach((obj: omAirCurtain) => {
                        if (!this._airCurtains.has(obj.airCurtainID)) {
                            this._airCurtains.set(
                                obj.airCurtainID,
                                AirCurtain.createFromObject(
                                    obj,
                                    undefined
                                )
                            );
                        }
                    });
                }
            }
        }
        return true;
    }

    public async loadUsers(userIDs: Array<string>): Promise<boolean> {
        // load in batch of 100 the user ID passed to the function
        const promises: Array<Promise<any>> = [];
        const keys: Array<string> = [];
        userIDs.forEach((userID) => {
            if (!this._users.has(userID)) {
                keys.push(userID);
                if (keys.length == 100) {
                    const getUsersRequest: GetRequest = {
                        objectType: 'user',
                        keys: [...keys],
                    };
                    promises.push(getObjects([getUsersRequest]));
                    keys.length = 0;
                }
            }
        });
        if (keys.length > 0) {
            const getUsersRequest: GetRequest = {
                objectType: 'user',
                keys: [...keys],
            };
            promises.push(getObjects([getUsersRequest]));
            keys.length = 0;
        }

        if (promises.length > 0) {
            const responses = await Promise.all(promises);
            for (let i = 0; i < responses.length; i++) {
                const response = responses[i][0];
                if (response) {
                    response.objects.forEach((obj: omUser) => {
                        if (!this._users.has(obj.userID)) {
                            this._users.set(
                                obj.userID,
                                User.createFromObject(obj)
                            );
                        }
                    });
                }
            }
        }
        return true;
    }

    public async loadIdentities(identityIDs: Array<string>): Promise<boolean> {
        // load in batch of 100 the identity IDs passed to the function
        const promises: Array<Promise<any>> = [];
        const keys: Array<string> = [];
        identityIDs.forEach((identityID) => {
            if (!this._identities.has(identityID)) {
                keys.push(identityID);
                if (keys.length == 100) {
                    const getIdentitiesRequest: GetRequest = {
                        objectType: 'identity',
                        keys: [...keys],
                    };
                    promises.push(getObjects([getIdentitiesRequest]));
                    keys.length = 0;
                }
            }
        });
        if (keys.length > 0) {
            const getIdentitiesRequest: GetRequest = {
                objectType: 'identity',
                keys: [...keys],
            };
            promises.push(getObjects([getIdentitiesRequest]));
            keys.length = 0;
        }

        if (promises.length > 0) {
            const responses = await Promise.all(promises);
            for (let i = 0; i < responses.length; i++) {
                const response = responses[i][0];
                if (response) {
                    response.objects.forEach((obj: omIdentity) => {
                        if (!this._identities.has(obj.identityID)) {
                            this._identities.set(
                                obj.identityID,
                                Identity.createFromObject(obj)
                            );
                        }
                    });
                }
            }
        }
        return true;
    }

    // public async loadAllLazyObjects(): Promise<boolean> {
    //     const promises: Array<Promise<any>> = [];

    //     let getUsers = false;
    //     const getUsersRequest: GetRequest = {
    //         objectType: 'user',
    //         keys: [],
    //     };
    //     this._userGroups.forEach((userGroup) => {
    //         userGroup.users.forEach((userID) => {
    //             if (
    //                 !getUsersRequest.keys.includes(userID) &&
    //                 !this._users.has(userID)
    //             ) {
    //                 getUsersRequest.keys.push(userID);
    //                 getUsers = true;
    //             }
    //         });
    //     });

    //     let getAirCurtains = false;
    //     const getAirCurtainsRequest: GetRequest = {
    //         objectType: 'airCurtain',
    //         keys: [],
    //     };
    //     this._airCurtainGroups.forEach((airCurtainGroup) => {
    //         airCurtainGroup.airCurtains.forEach((airCurtainID) => {
    //             if (
    //                 !getAirCurtainsRequest.keys.includes(airCurtainID) &&
    //                 !this._airCurtains.has(airCurtainID)
    //             ) {
    //                 const getShadowRequest: GetShadowRequest = { airCurtainID };
    //                 promises.push(getShadow(getShadowRequest));
    //                 getAirCurtainsRequest.keys.push(airCurtainID);
    //                 getAirCurtains = true;
    //             }
    //         });
    //     });

    //     let getInvitations = false;
    //     const getInvitationsRequest: GetRequest = {
    //         objectType: 'invitation',
    //         keys: [],
    //     };
    //     this._organization.invitations.forEach((invitationID) => {
    //         if (!getInvitationsRequest.keys.includes(invitationID)) {
    //             getInvitationsRequest.keys.push(invitationID);
    //             getInvitations = true;
    //         }
    //     });

    //     const requests: Array<GetRequest> = [];
    //     if (getUsers) requests.push(getUsersRequest);
    //     if (getAirCurtains) requests.push(getAirCurtainsRequest);
    //     if (getInvitations) requests.push(getInvitationsRequest);

    //     if (requests.length == 0) return true;

    //     promises.push(getObjects(requests));

    //     const responses = await Promise.all(promises);

    //     const shadows: Map<string, AirCurtainShadow2> = new Map<
    //         string,
    //         AirCurtainShadow2
    //     >();
    //     if (getAirCurtains) {
    //         for (let i = 0; i < responses.length - 1; i++) {
    //             shadows.set(responses[i].airCurtainID, responses[i]);
    //         }
    //     }

    //     const getObjectsResponses = responses[responses.length - 1]; // last element

    //     getObjectsResponses.forEach((response: any) => {
    //         switch (response.objectType) {
    //             case 'user':
    //                 response.objects.forEach((obj: omUser) => {
    //                     this._users.set(obj.userID, User.createFromObject(obj));
    //                 });
    //                 break;
    //             case 'airCurtain':
    //                 response.objects.forEach((obj: omAirCurtain) => {
    //                     if (!this._airCurtains.has(obj.airCurtainID)) {
    //                         this._airCurtains.set(
    //                             obj.airCurtainID,
    //                             AirCurtain.createFromObject(
    //                                 obj,
    //                                 shadows.get(obj.airCurtainID),
    //                                 undefined
    //                             )
    //                         ); // installation can't exist because the air curtain is only in mobile app
    //                     }
    //                 });
    //                 break;
    //             case 'invitation':
    //                 response.objects.forEach((obj: omInvitation) => {
    //                     this._invitations.set(
    //                         obj.invitationID,
    //                         Invitation.createFromObject(obj)
    //                     );
    //                 });
    //                 break;
    //         }
    //     });

    //     return true;
    // }

    public async getCognitoUser(email: string): Promise<any> {
        const response = await getCognitoUserAPI({ email });
        if (response.result) {
            return response.user;
        }
        console.log(response.error);
        return undefined;
    }

    public async invite(user: User, email: string): Promise<any> {
        console.log('inviting ' + email + ' to join organization');
        if (user.invitationID != undefined) {
            return {
                result: false,
                error: 'user already has an active invitation',
                invitationID: user.invitationID,
            };
        }
        const cognitoUser = await this.getCognitoUser(email);
        if (cognitoUser != undefined) {
            return {
                result: false,
                error:
                    "cognito user arleady using this email exists, \
                load identity associated to it. if it doesn't exists you may create it and link it with user \
                else check if account is the same as current one, \
                if so you may link the identity with the user, else, \
                reject the invitation as the cognito user is in another account",
                cognitoUser: cognitoUser,
            };
        }

        // create invitation
        const invitation = new Invitation(email);
        invitation.userID = user.userID;
        invitation.accountID = this._account.accountID;
        invitation.organizationID = this._organization.organizationId;
        invitation.fromUserID = this._user.userID;
        invitation.invitationDate = new Date();

        // link to user and organization
        user.invitationID = invitation.invitationID;
        this._organization.addInvitation(invitation);

        //save invitation, user and organization
        const promises: Array<Promise<boolean>> = [];
        promises.push(invitation.save());
        promises.push(this._organization.save());
        promises.push(user.save());
        const results = await Promise.all(promises);
        if (!results[0])
            return { result: false, error: "couldn't save invitation" };
        if (!results[1])
            return { result: false, error: "couldn't save organization" };
        if (!results[2]) return { result: false, error: "couldn't save user" };

        // send email
        const result = await invitation.sendInvitationEmail();

        if (result) {
            return {
                result: true,
                invitationID: invitation.invitationID,
            };
        }

        return { result: false, error: "couldn't send email" };
    }

    public getUserAirCurtainsFromMobileAPI(): Promise<Array<AirCurtain>> {
        return new Promise((resolve) => {
            getListAirCurtainUserRelation().then((airCurtainShadows) => {
                const airCurtains: Array<AirCurtain> = [];
                if (airCurtainShadows) {
                    for (const airCurtainID in airCurtainShadows) {
                        const airCurtainShdow: AirCurtainShadow =
                            airCurtainShadows[airCurtainID];
                        const airCurtain: AirCurtain = new AirCurtain(
                            airCurtainID
                        );
                        airCurtain.model = airCurtainShdow.model;
                        airCurtain.name = airCurtainShdow.name;
                        airCurtain.heaterPower = airCurtainShdow.heaterPower;
                        airCurtains.push(airCurtain);
                    }
                }
                resolve(airCurtains);
            });
        });
    }
}
